import * as React from 'react';
import { useContext, useState, useEffect } from 'react';

import ModalDefault from './ModalDefault';
import { CalendarPicker } from '@mui/x-date-pickers/CalendarPicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import Button from '../button/Button';

import { d_flex_center, d_flex, dir_column } from '../../styles/common';
import { styled, Box } from '@mui/material';
import { ModalContext } from '../../provider/ModalProvider';
import { setLocaleDateString } from '../../utils/tools';

const CustomDatePicker = styled(CalendarPicker)(({ theme, view }) => {
  let year_style = {};
  if (view === 'year') {
    year_style = {
      '& > div': {
        '&:first-of-type': {
          display: 'none',
        },
        '&:last-of-type': {
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '& > div': {
            '& > div': {
              display: 'flex',
              flexDirection: 'column-reverse',
              margin: '0',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              '& > div': {
                width: '100%',
                button: {
                  height: '3.25rem',
                  width: '100%',
                  borderRadius: '0',
                  margin: '0',
                  padding: '1rem 0',
                  fontSize: '1.2rem',
                  fontWeight: '500',
                  ...d_flex_center,
                  '&.Mui-selected': {
                    backgroundColor: '#e0e0e0',
                    color: theme.palette.black.main,
                  },
                },
              },
            },
          },
        },
      },
    };
  }
  return {
    width: '24rem',
    height: '23.714rem',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& > div': {
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      fontSize: '1rem !important',
      marginTop: '15px',
      '&:first-of-type': {
        position: 'relative',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '& > div': {
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '&:first-of-type': {
            position: 'relative',
            right: '0.95rem',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            width: '100%',
            display: 'flex',
            justifyContent: 'center',

            '& > div': {
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              '&:first-of-type': {
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                width: '5rem',
                textAlign: 'center',
              },
            },

            button: {
              display: 'none',
            },
          },
          '&:last-of-type': {
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            width: '100%',
            height: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 0.5rem',
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            '& > button': {
              padding: '0',
              width: '2.5rem',
              height: '2.5rem',
            },
          },
        },
      },

      '.PrivatePickersSlideTransition-root': {
        minHeight: '238px',
        height: '238px',
        '.MuiPickersDay-root': {
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: 'rgba(0, 0, 0, 0.87)',

            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      '&:last-of-type': {
        height: 'calc(100% - 30px)',
        '& > div': {
          height: '100%',
          '& > div:first-of-type': {
            height: '2.5rem',
            gap: '0.1rem 0rem',
            '& > span': {
              width: '2.5rem',
              height: '2.5rem',
            },
          },
          '& > div:last-of-type': {
            // height: 'calc(100% - 3rem)',
            height: 'auto',
            '& > div': {
              '& > div': {
                margin: '0',
                gap: '0.1rem 0rem',
                '& > div': {
                  '& > div': {
                    width: '2.5rem',
                    height: '2.5rem',
                  },
                  '& > button': {
                    width: '2.5rem',
                    height: '2.5rem',
                  },
                },
              },
            },
          },
        },
      },
    },
    ...year_style,
    margin: '0',
  };
});

const StyledModalCalendar = styled(Box)(props => ({
  minWidth: '24rem',
  backgroundColor: props.theme.palette.white.main,
  ...d_flex_center,
  ...dir_column,
}));

const StyledTitle = styled(Box)(props => ({
  width: '100%',
  height: '6rem',
  backgroundColor: '#FF453B !important',
  color: props.theme.palette.white.main,
  padding: '0 1rem',
  justifyContent: 'center',
  ...d_flex,
  ...dir_column,
}));

function ModalCalendar() {
  const { modal_calendar } = useContext(ModalContext);
  const [value, setValue] = useState<Date | null | unknown>(null);
  const [localeDate, setLocaleDate] = useState(setLocaleDateString(new Date(), 'kor'));
  const [curYear, setCurYear] = useState('');
  const [maxDate, setMaxDate] = useState(new Date('9999-12-31'));
  const [defaultMonth, setDefaultMonth] = useState<Date | null>(null);

  useEffect(() => {
    if (modal_calendar.data.visible) {
      if (modal_calendar.data.type === 'year') {
        setMaxDate(new Date());
      } else {
        setMaxDate(modal_calendar.data.max);
        if (modal_calendar.data.default) {
          setDefaultMonth(modal_calendar.data.default);
        }
      }
      if (
        modal_calendar.data.date[modal_calendar.data.type] ||
        modal_calendar.data.date[modal_calendar.data.type] === null
      ) {
        setValue(modal_calendar.data.date[modal_calendar.data.type]);

        setLocaleDate(setLocaleDateString(modal_calendar.data.date[modal_calendar.data.type], 'kor'));
      }
    }
  }, [modal_calendar.data.visible]);

  useEffect(() => {
    if (localeDate) {
      setCurYear(localeDate.slice(0, 4));
    }
  }, [localeDate]);

  const handleChange = (date: Date | null | unknown, type: string) => {
    setValue(date);
    if (type == 'date') {
      setLocaleDate(setLocaleDateString(new Date(`${date}`), 'kor'));
      modal_calendar.setModalCalendarDataDateType(new Date(`${date}`), modal_calendar.data.type);
      modal_calendar.closeModalCalendar();
    }
  };
  const last_year = new Date().getFullYear() - 1;

  return (
    <>
      <ModalDefault visible={modal_calendar.data.visible} onClose={modal_calendar.closeModalCalendar}>
        <StyledModalCalendar>
          <StyledTitle>
            <Box component={'h4'} sx={{ color: 'gray_3.main' }}>
              {curYear}
            </Box>
            <Box component={'h2'}>{localeDate}</Box>
          </StyledTitle>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CustomDatePicker
              defaultCalendarMonth={defaultMonth}
              view={modal_calendar.data.type === 'year' ? 'year' : 'day'}
              minDate={
                modal_calendar.data.type === 'year'
                  ? new Date(`${last_year}`)
                  : modal_calendar.data.min
                  ? modal_calendar.data.min
                  : null
              }
              maxDate={maxDate}
              date={value}
              onChange={(date: Date | null | unknown) => handleChange(date, 'date')}
              onMonthChange={(date: Date | null | unknown) => handleChange(date, 'month')}
              allowSameDateSelection
            />
          </LocalizationProvider>

          <Button color='black' onClick={modal_calendar.closeModalCalendar} sx={{ width: 1, pt: '1rem', pb: '1rem' }}>
            취소
          </Button>
        </StyledModalCalendar>
      </ModalDefault>
    </>
  );
}

export default ModalCalendar;
